'use client';
import type {AmenityCategory} from '#/cms/schemas/amenity-category.schema';
import {type CategoryClass} from '#/cms/schemas/category-class.schema';
import {type PlaceSummary} from '#/cms/schemas/place-summary.schema';
import {type SightCategory} from '#/cms/schemas/sight-category.schema';
import {type Sight} from '#/cms/schemas/sight.schema';
import useAnalytics, {AnalyticsEventAction, AnalyticsEventCategory} from '#/features/analytics/useAnalytics';
import {CategoryBox} from '#/features/components/CategoryBox';
import {IconButton} from '#/features/components/IconButton';
import {PlaceFilter} from '#/features/components/PlaceFilter';
import SightResult from '#/features/components/SightResult';
import {Heading1, Heading2} from '#/features/components/Text';
import TextSearchBox from '#/features/components/TextSearchBox';
import {usePlaceStore} from '#/features/store/placeStore';
import {useUiStrings} from '#/features/store/uiStrings';
import {Radius, type RadiusOption} from '#/utils/geo';
import {
  AMENITIES_SEARCH_PARAM_KEY,
  CATEGORIES_SEARCH_PARAM_KEY,
  PLACE_SEARCH_PARAM_KEY,
  RADIUS_SEARCH_PARAM_KEY,
  usePanelView,
  useURLFilterParams,
  useUpdateQueryParams,
} from '#/utils/queryParams';
import updateActiveFilters from '#/utils/updateActiveFilters';
import {useCallback, useMemo, useState} from 'react';
import {SightsMap} from './SightsMap';

type Props = {
  sights: Sight[];
  categoryClasses: CategoryClass[];
  sightCategories: SightCategory[];
  amenityCategories: AmenityCategory[];
};

export const SightsPanel = (props: Props) => {
  const {sights, categoryClasses, sightCategories, amenityCategories} = props;
  const {registerEvent} = useAnalytics();
  const {placeMap} = usePlaceStore();
  const uiStrings = useUiStrings();
  const urlFilterParams = useURLFilterParams();
  const {panelView, togglePanelView} = usePanelView();
  const updateQueryParams = useUpdateQueryParams();
  const [searchText, setSearchText] = useState('');

  const selectedPlace = urlFilterParams.placeId;
  const sightPlaces = useMemo(() => {
    const result = new Set<PlaceSummary['id']>();
    for (const sight of sights) {
      for (const placeId of sight.placeIds ?? []) {
        result.add(placeId);
      }
    }
    return [...result];
  }, [sights]);

  const handlePlaceSelect = useCallback(
    (placeId: PlaceSummary['id'] | null) => {
      const radius = urlFilterParams.radius ?? Radius['10Miles'];
      updateQueryParams({
        [PLACE_SEARCH_PARAM_KEY]: placeId,
        [RADIUS_SEARCH_PARAM_KEY]: !!placeId ? radius : null,
      });
      if (placeId) {
        const place = placeMap.get(placeId);
        if (!place) throw new Error(`Place ${placeId} not found`);
        registerEvent({
          category: AnalyticsEventCategory.Sights,
          action: AnalyticsEventAction.FilterByPlace,
          label: place.title ?? place.id,
          radius,
          placeId,
        });
      }
    },
    [placeMap, registerEvent, updateQueryParams, urlFilterParams.radius]
  );

  const handleRadiusSelect = useCallback(
    (radiusOption: RadiusOption) => {
      updateQueryParams({
        [RADIUS_SEARCH_PARAM_KEY]: radiusOption.id,
      });
      registerEvent({
        category: AnalyticsEventCategory.Sights,
        action: AnalyticsEventAction.FilterByPlace,
        label: radiusOption.title,
        radius: radiusOption.id,
        placeId: urlFilterParams.placeId ?? '',
      });
    },
    [registerEvent, updateQueryParams, urlFilterParams.placeId]
  );

  const handleCategoryFilterChange = useCallback(
    (change: Record<string, boolean>) => {
      const activeCategories = updateActiveFilters(urlFilterParams.categories, change);
      updateQueryParams({[CATEGORIES_SEARCH_PARAM_KEY]: activeCategories});
      for (const [categoryId, active] of Object.entries(change)) {
        if (active) {
          const category = sightCategories.find(c => c.id === categoryId);
          if (!category) throw new Error(`Sight category ${categoryId} not found`);
          registerEvent({
            category: AnalyticsEventCategory.Sights,
            action: AnalyticsEventAction.FilterByCategory,
            label: category.title ?? category.id,
            categoryId,
          });
        }
      }
    },
    [registerEvent, sightCategories, updateQueryParams, urlFilterParams.categories]
  );

  const handleAmenityFilterChange = useCallback(
    (change: Record<string, boolean>) => {
      const activeAmenities = updateActiveFilters(urlFilterParams.amenities, change);
      updateQueryParams({[AMENITIES_SEARCH_PARAM_KEY]: activeAmenities});
      for (const [categoryId, active] of Object.entries(change)) {
        if (active) {
          const category = amenityCategories.find(c => c.id === categoryId);
          if (!category) throw new Error(`Amenity category ${categoryId} not found`);
          registerEvent({
            category: AnalyticsEventCategory.Sights,
            action: AnalyticsEventAction.FilterByAmenity,
            label: category.title ?? category.id,
            categoryId,
          });
        }
      }
    },
    [amenityCategories, registerEvent, updateQueryParams, urlFilterParams.amenities]
  );

  const filteredSights = useMemo(() => {
    const activeCategories = urlFilterParams.categories;
    const activeAmenities = urlFilterParams.amenities;

    let result = sights;
    if (activeCategories.length !== 0) {
      result = result.filter(sight => sight.sightCategories?.some(category => activeCategories.includes(category.id)));
    }
    if (activeAmenities.length !== 0) {
      result = result.filter(sight => sight.amenityCategories?.some(amenity => activeAmenities.includes(amenity.id)));
    }

    if (selectedPlace) {
      result = result.filter(sight => sight.placeIds?.some(placeId => placeId === selectedPlace));
    }
    if (searchText.length > 0) {
      const currentSearchText = searchText.toLowerCase();
      result = result.filter(
        sight =>
          (sight.title ?? sight.id).toLowerCase().includes(currentSearchText) ||
          sight.description?.toLowerCase().includes(currentSearchText)
      );
    }
    return result;
  }, [urlFilterParams.categories, urlFilterParams.amenities, sights, selectedPlace, searchText]);

  const sightCategoryClasses = useMemo(
    () =>
      categoryClasses.filter(categoryClass =>
        sightCategories.some(category => !!category.class && category.class === categoryClass.id)
      ),
    [categoryClasses, sightCategories]
  );

  const amenityCategoryClasses = useMemo(
    () =>
      categoryClasses.filter(categoryClass =>
        amenityCategories.some(category => !!category.class && category.class === categoryClass.id)
      ),
    [categoryClasses, amenityCategories]
  );

  return (
    <div className="flex flex-grow flex-row">
      <div className="flex w-[25rem] flex-col gap-y-4 px-4 py-14">
        <Heading2>Filter</Heading2>
        <TextSearchBox
          placeholder={uiStrings.sights.searchPlaceholder}
          text={searchText}
          onTextChange={setSearchText}
          id={'sights-search'}
        />
        <PlaceFilter
          availablePlaces={sightPlaces}
          selectedPlace={selectedPlace}
          selectedRadius={urlFilterParams.radius}
          onSelectPlace={handlePlaceSelect}
          onSelectRadius={handleRadiusSelect}
        />
        {sightCategories.length > 0 && (
          <CategoryBox<SightCategory>
            title={uiStrings.sights.sightCategories}
            categoryClasses={sightCategoryClasses}
            categories={sightCategories}
            activeCategoryIds={urlFilterParams.categories}
            onFilterChange={handleCategoryFilterChange}
          />
        )}
        {amenityCategories.length > 0 && (
          <CategoryBox<AmenityCategory>
            title={uiStrings.amenities.title}
            categoryClasses={amenityCategoryClasses}
            categories={amenityCategories}
            activeCategoryIds={urlFilterParams.amenities}
            onFilterChange={handleAmenityFilterChange}
          />
        )}
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex flex-grow-0 flex-row justify-between px-12 pt-12">
          <Heading1>
            <span className="font-normal">{uiStrings.general.explore}</span> {uiStrings.sights.title}
          </Heading1>
          <IconButton
            label={panelView === 'grid' ? uiStrings.general.mapButtonLabel : uiStrings.general.gridButtonLabel}
            active={true}
            pepIcon={panelView === 'grid' ? 'Maps' : 'Grid'}
            iconHoverColor="secondary"
            onClick={togglePanelView}
          />
        </div>
        {panelView === 'grid' && (
          <div className="grid gap-4 px-12 py-8 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
            {filteredSights.map(sight => (
              <SightResult key={`sight-result-${sight.id}`} sight={sight} />
            ))}
          </div>
        )}
        {panelView === 'map' && (
          <div className="border-3 mx-12 my-8 flex flex-grow overflow-hidden rounded-2xl bg-foreground">
            <SightsMap sights={filteredSights} sightCategories={sightCategories} />
          </div>
        )}
      </div>
    </div>
  );
};
